import { useState, useRef, useCallback, useMemo } from 'react';
import { ChevronRightIcon } from '@/components/ui/Icons';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import {
  ALL_COLUMN_DEFS,
  DEFAULT_COMBOS,
  MENU_COLUMN_LABELS,
  NO_HIDE_IDS,
  type ComboConfig,
} from '@/hooks/useTableColumns';
import type { TableColumn } from '@/hooks/useTableColumns';
import { PROJECT_TYPES, PROJECT_TYPE_DISPLAY, type ProjectTypeValue } from '@/utils/projectTypeColors';
import {
  PLANNER_NAME_BADGE_MODES_ORDER,
  PLANNER_NAME_BADGE_MODE_LABELS,
  type PlannerNameBadgeMode,
} from '@/hooks/usePlannerNameBadgeMode';
import { Switch } from '@/components/ui/Switch/Switch';
import { ChoiceDialog } from '@/components/ui/ChoiceDialog/ChoiceDialog';
import './TableContextMenu.css';

/** Только дедлайн (отдельно от «Даты») — исторически не в списке комбо в контекстном меню */
const NO_COMBO_DEADLINE_ID = new Set(['deadline']);
const COMBO_IDS = new Set(Object.keys(DEFAULT_COMBOS));

interface TableContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorPosition: { x: number; y: number } | null;
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Область, клик/выделение в которой не закрывает меню (например контейнер таблицы) */
  excludeRefs?: React.RefObject<HTMLElement | null>[];
  anchorColumnId: string | null;
  columns: TableColumn[];
  atomicOrder: string[];
  combos: Record<string, ComboConfig>;
  visibility: Record<string, boolean>;
  toggleColumnVisibility: (id: string) => void;
  toggleComboSelection: (anchorColumnId: string, columnId: string) => void;
  getComboColumnsFor: (columnId: string) => string[];
  getComboableColumnsForAnchor?: (anchorColumnId: string) => { id: string; title: string }[];
  typeBadgesEnabled: Record<ProjectTypeValue, boolean>;
  toggleTypeBadge: (type: ProjectTypeValue) => void;
  plannerNameBadgeMode: PlannerNameBadgeMode;
  setPlannerNameBadgeMode: (mode: PlannerNameBadgeMode) => void;
  resetColumnOrderToDefault?: () => void;
  expandAllCombos?: () => void;
  /** Динамический набор столбцов, запрещённых для комбо (зависит от компактного режима). */
  noComboColumnIds: Set<string>;
  compactReaders: boolean;
  compactStudio: boolean;
  onCompactReadersChange: (value: boolean) => void;
  onCompactStudioChange: (value: boolean) => void;
}

export function TableContextMenu({
  isOpen,
  onClose,
  anchorPosition,
  anchorRef,
  excludeRefs,
  anchorColumnId,
  columns,
  atomicOrder,
  combos,
  visibility,
  toggleColumnVisibility,
  toggleComboSelection,
  getComboColumnsFor,
  getComboableColumnsForAnchor,
  typeBadgesEnabled,
  toggleTypeBadge,
  plannerNameBadgeMode,
  setPlannerNameBadgeMode,
  resetColumnOrderToDefault,
  expandAllCombos,
  noComboColumnIds,
  compactReaders,
  compactStudio,
  onCompactReadersChange,
  onCompactStudioChange,
}: TableContextMenuProps) {
  const [submenuType, setSubmenuType] = useState<'columns' | 'combo' | 'settings' | null>(null);
  const [submenuEl, setSubmenuEl] = useState<HTMLElement | null>(null);
  const [typeBadgesAnchorEl, setTypeBadgesAnchorEl] = useState<HTMLElement | null>(null);
  const [plannerBadgesAnchorEl, setPlannerBadgesAnchorEl] = useState<HTMLElement | null>(null);
  const [columnsVisibilityAnchorEl, setColumnsVisibilityAnchorEl] = useState<HTMLElement | null>(null);
  const [columnsCompactAnchorEl, setColumnsCompactAnchorEl] = useState<HTMLElement | null>(null);
  const [resetLayoutDialogOpen, setResetLayoutDialogOpen] = useState(false);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = useCallback(() => {
    submenuCloseTimerRef.current = setTimeout(() => {
      setSubmenuType(null);
      setSubmenuEl(null);
      setTypeBadgesAnchorEl(null);
      setPlannerBadgesAnchorEl(null);
      setColumnsVisibilityAnchorEl(null);
      setColumnsCompactAnchorEl(null);
      submenuCloseTimerRef.current = null;
    }, 150);
  }, []);

  const allAtomicComboable = useMemo(
    () =>
      Object.keys(ALL_COLUMN_DEFS).filter(
        (id) => !noComboColumnIds.has(id) && !NO_COMBO_DEADLINE_ID.has(id) && !COMBO_IDS.has(id)
      ),
    [noComboColumnIds]
  );

  const orderIndex = (id: string) => {
    const i = atomicOrder.indexOf(id);
    return i === -1 ? 99999 : i;
  };

  const columnsSorted = [...columns]
    .filter((col) => !NO_HIDE_IDS.has(col.id))
    .sort((a, b) => orderIndex(a.id) - orderIndex(b.id));

  const comboColumns = anchorColumnId ? getComboColumnsFor(anchorColumnId) : [];
  const comboColumnsSet = new Set(comboColumns);

  const inOtherCombo = new Set<string>();
  for (const [comboId, config] of Object.entries(combos)) {
    const isCurrentCombo = anchorColumnId && (comboId === anchorColumnId || config.childIds.includes(anchorColumnId));
    if (!isCurrentCombo) config.childIds.forEach((id) => inOtherCombo.add(id));
  }

  const comboableForAnchor = getComboableColumnsForAnchor && anchorColumnId
    ? getComboableColumnsForAnchor(anchorColumnId)
    : null;
  const isVisible = (id: string) => visibility[id] === true;
  const otherColumns = comboableForAnchor
    ? comboableForAnchor
        .filter((c) => !comboColumnsSet.has(c.id) && !inOtherCombo.has(c.id) && isVisible(c.id))
        .map((c) => c.id)
    : allAtomicComboable.filter((id) => {
        if (comboColumnsSet.has(id)) return false;
        if (inOtherCombo.has(id)) return false;
        if (!isVisible(id)) return false;
        return true;
      });
  const comboMenuOrder = [...comboColumns.filter((id) => isVisible(id)), ...otherColumns].sort(
    (a, b) => orderIndex(a) - orderIndex(b)
  );

  return (
    <>
    <ContextMenu isOpen={isOpen} onClose={onClose} anchorPosition={anchorPosition} anchorRef={anchorRef} excludeRefs={excludeRefs}>
      <ul className="context-menu__list table-context-menu__list">
        <li
          className="context-menu__list-item table-context-menu__item_with-sub"
          onMouseEnter={(e) => {
            cancelSubmenuClose();
            setSubmenuType('columns');
            setSubmenuEl(e.currentTarget);
            setTypeBadgesAnchorEl(null);
            setPlannerBadgesAnchorEl(null);
            setColumnsVisibilityAnchorEl(null);
            setColumnsCompactAnchorEl(null);
          }}
          onMouseLeave={() => scheduleSubmenuClose()}
        >
          <button type="button" className="context-menu__action context-menu__action_with-sub">
            <span className="context-menu__action-text">Столбцы</span>
            <ChevronRightIcon size={16} className="context-menu__action-chevron" />
          </button>
          {submenuType === 'columns' && submenuEl && (
            <ContextMenuSubmenu
              anchorEl={submenuEl}
              onMouseEnter={cancelSubmenuClose}
              onMouseLeave={() => {
                setSubmenuType(null);
                setSubmenuEl(null);
                setColumnsVisibilityAnchorEl(null);
                setColumnsCompactAnchorEl(null);
              }}
            >
              <ul className="context-menu__list">
                <li
                  className="context-menu__list-item table-context-menu__item_with-sub"
                  onMouseEnter={(e) => {
                    cancelSubmenuClose();
                    setColumnsCompactAnchorEl(null);
                    setColumnsVisibilityAnchorEl(e.currentTarget);
                  }}
                  onMouseLeave={() => setColumnsVisibilityAnchorEl(null)}
                >
                  <button type="button" className="context-menu__action context-menu__action_with-sub">
                    <span className="context-menu__action-text">Видимость</span>
                    <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                  </button>
                  {columnsVisibilityAnchorEl && (
                    <ContextMenuSubmenu
                      anchorEl={columnsVisibilityAnchorEl}
                      zIndex={1200}
                      onMouseEnter={cancelSubmenuClose}
                      onMouseLeave={() => setColumnsVisibilityAnchorEl(null)}
                    >
                      <ul className="context-menu__list">
                        {columnsSorted.map((col) => (
                          <li key={col.id} className="context-menu__list-item">
                            <Switch
                              className="context-menu__label"
                              checked={col.visible}
                              onCheckedChange={() => toggleColumnVisibility(col.id)}
                              onClick={(e) => e.stopPropagation()}
                              label={
                                <>
                                  <span
                                    className={`context-menu__label-text ${!col.visible && !col.inCombo ? 'context-menu__label-text_muted' : ''}`}
                                  >
                                    {col.title || col.id}
                                  </span>
                                  {col.inCombo ? (
                                    <span className="table-context-menu__combo-badge">В комбо</span>
                                  ) : null}
                                </>
                              }
                            />
                          </li>
                        ))}
                      </ul>
                    </ContextMenuSubmenu>
                  )}
                </li>
                <li
                  className="context-menu__list-item table-context-menu__item_with-sub"
                  onMouseEnter={(e) => {
                    cancelSubmenuClose();
                    setColumnsVisibilityAnchorEl(null);
                    setColumnsCompactAnchorEl(e.currentTarget);
                  }}
                  onMouseLeave={() => setColumnsCompactAnchorEl(null)}
                >
                  <button type="button" className="context-menu__action context-menu__action_with-sub">
                    <span className="context-menu__action-text">Компактный режим</span>
                    <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                  </button>
                  {columnsCompactAnchorEl && (
                    <ContextMenuSubmenu
                      anchorEl={columnsCompactAnchorEl}
                      zIndex={1200}
                      onMouseEnter={cancelSubmenuClose}
                      onMouseLeave={() => setColumnsCompactAnchorEl(null)}
                    >
                      <ul className="context-menu__list">
                        <li className="context-menu__list-item">
                          <Switch
                            className="context-menu__label"
                            checked={compactReaders}
                            onCheckedChange={() => onCompactReadersChange(!compactReaders)}
                            onClick={(e) => e.stopPropagation()}
                            label={<span className="context-menu__label-text">Чтецы</span>}
                          />
                        </li>
                        <li className="context-menu__list-item">
                          <Switch
                            className="context-menu__label"
                            checked={compactStudio}
                            onCheckedChange={() => onCompactStudioChange(!compactStudio)}
                            onClick={(e) => e.stopPropagation()}
                            label={<span className="context-menu__label-text">Контрагенты</span>}
                          />
                        </li>
                      </ul>
                    </ContextMenuSubmenu>
                  )}
                </li>
                <li className="context-menu__list-item">
                  <button
                    type="button"
                    className="context-menu__action"
                    onClick={() => {
                      expandAllCombos?.();
                      onClose();
                    }}
                  >
                    <span className="context-menu__action-text">Разобрать все комбо</span>
                  </button>
                </li>
                <li className="context-menu__list-item">
                  <button
                    type="button"
                    className="context-menu__action"
                    onClick={() => {
                      onClose();
                      setResetLayoutDialogOpen(true);
                    }}
                  >
                    <span className="context-menu__action-text">Сбросить все настройки</span>
                  </button>
                </li>
              </ul>
            </ContextMenuSubmenu>
          )}
        </li>
        <li
          className="context-menu__list-item table-context-menu__item_with-sub"
          onMouseEnter={(e) => {
            cancelSubmenuClose();
            setSubmenuType('combo');
            setSubmenuEl(e.currentTarget);
            setTypeBadgesAnchorEl(null);
            setPlannerBadgesAnchorEl(null);
            setColumnsVisibilityAnchorEl(null);
            setColumnsCompactAnchorEl(null);
          }}
          onMouseLeave={() => scheduleSubmenuClose()}
        >
          <button type="button" className="context-menu__action context-menu__action_with-sub">
            <span className="context-menu__action-text">Комбо</span>
            <ChevronRightIcon size={16} className="context-menu__action-chevron" />
          </button>
          {submenuType === 'combo' && submenuEl && anchorColumnId && !noComboColumnIds.has(anchorColumnId) && (
            <ContextMenuSubmenu
              anchorEl={submenuEl}
              onMouseEnter={cancelSubmenuClose}
              onMouseLeave={() => {
                setSubmenuType(null);
                setSubmenuEl(null);
              }}
            >
              <ul className="context-menu__list">
                {comboMenuOrder.map((id) => {
                  const title = (MENU_COLUMN_LABELS[id] ?? ALL_COLUMN_DEFS[id]?.title) || id;
                  const isInCombo = comboColumnsSet.has(id);
                  return (
                    <li key={id} className="context-menu__list-item">
                      <Switch
                        className="context-menu__label"
                        checked={isInCombo}
                        onCheckedChange={() => toggleComboSelection(anchorColumnId, id)}
                        onClick={(e) => e.stopPropagation()}
                        label={<span className="context-menu__label-text">{title}</span>}
                      />
                    </li>
                  );
                })}
              </ul>
            </ContextMenuSubmenu>
          )}
        </li>
        <li
          className="context-menu__list-item table-context-menu__item_with-sub"
          onMouseEnter={(e) => {
            cancelSubmenuClose();
            setSubmenuType('settings');
            setSubmenuEl(e.currentTarget);
            setTypeBadgesAnchorEl(null);
            setPlannerBadgesAnchorEl(null);
            setColumnsVisibilityAnchorEl(null);
            setColumnsCompactAnchorEl(null);
          }}
          onMouseLeave={() => scheduleSubmenuClose()}
        >
          <button type="button" className="context-menu__action context-menu__action_with-sub">
            <span className="context-menu__action-text">Настройки</span>
            <ChevronRightIcon size={16} className="context-menu__action-chevron" />
          </button>
          {submenuType === 'settings' && submenuEl && (
            <ContextMenuSubmenu
              anchorEl={submenuEl}
              onMouseEnter={cancelSubmenuClose}
              onMouseLeave={() => {
                setSubmenuType(null);
                setSubmenuEl(null);
                setTypeBadgesAnchorEl(null);
                setPlannerBadgesAnchorEl(null);
                setColumnsVisibilityAnchorEl(null);
                setColumnsCompactAnchorEl(null);
              }}
            >
              <ul className="context-menu__list">
                <li
                  className="context-menu__list-item table-context-menu__item_with-sub"
                  onMouseEnter={(e) => {
                    cancelSubmenuClose();
                    setPlannerBadgesAnchorEl(null);
                    setTypeBadgesAnchorEl(e.currentTarget);
                  }}
                  onMouseLeave={() => setTypeBadgesAnchorEl(null)}
                >
                  <button type="button" className="context-menu__action context-menu__action_with-sub">
                    <span className="context-menu__action-text">Табличка типа проекта</span>
                    <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                  </button>
                  {typeBadgesAnchorEl && (
                    <ContextMenuSubmenu
                      anchorEl={typeBadgesAnchorEl}
                      zIndex={1200}
                      onMouseEnter={cancelSubmenuClose}
                      onMouseLeave={() => setTypeBadgesAnchorEl(null)}
                    >
                      <ul className="context-menu__list">
                        {PROJECT_TYPES.map((typeKey) => (
                          <li key={typeKey} className="context-menu__list-item">
                            <Switch
                              className="context-menu__label"
                              checked={typeBadgesEnabled[typeKey]}
                              onCheckedChange={() => toggleTypeBadge(typeKey)}
                              onClick={(e) => e.stopPropagation()}
                              label={<span className="context-menu__label-text">{PROJECT_TYPE_DISPLAY[typeKey].text}</span>}
                            />
                          </li>
                        ))}
                      </ul>
                    </ContextMenuSubmenu>
                  )}
                </li>
                <li
                  className="context-menu__list-item table-context-menu__item_with-sub"
                  onMouseEnter={(e) => {
                    cancelSubmenuClose();
                    setTypeBadgesAnchorEl(null);
                    setPlannerBadgesAnchorEl(e.currentTarget);
                  }}
                  onMouseLeave={() => setPlannerBadgesAnchorEl(null)}
                >
                  <button type="button" className="context-menu__action context-menu__action_with-sub">
                    <span className="context-menu__action-text">Иконка статуса планера</span>
                    <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                  </button>
                  {plannerBadgesAnchorEl && (
                    <ContextMenuSubmenu
                      anchorEl={plannerBadgesAnchorEl}
                      zIndex={1200}
                      onMouseEnter={cancelSubmenuClose}
                      onMouseLeave={() => setPlannerBadgesAnchorEl(null)}
                    >
                      <ul className="context-menu__list">
                        {PLANNER_NAME_BADGE_MODES_ORDER.map((mode) => (
                          <li key={mode} className="context-menu__list-item">
                            <Switch
                              className="context-menu__label"
                              checked={plannerNameBadgeMode === mode}
                              onCheckedChange={(next) => {
                                if (next) setPlannerNameBadgeMode(mode);
                                else if (plannerNameBadgeMode === mode) setPlannerNameBadgeMode('all');
                              }}
                              onClick={(e) => e.stopPropagation()}
                              label={
                                <span className="context-menu__label-text">{PLANNER_NAME_BADGE_MODE_LABELS[mode]}</span>
                              }
                            />
                          </li>
                        ))}
                      </ul>
                    </ContextMenuSubmenu>
                  )}
                </li>
              </ul>
            </ContextMenuSubmenu>
          )}
        </li>
      </ul>
    </ContextMenu>
    <ChoiceDialog
      open={resetLayoutDialogOpen}
      onClose={() => setResetLayoutDialogOpen(false)}
      title="Сброс настроек таблицы"
      description="Все настройки столбцов и комбо-ячеек будут сброшены к значениям по умолчанию."
      actions={[
        {
          label: 'Отмена',
          variant: 'outlined',
          onClick: () => setResetLayoutDialogOpen(false),
        },
        {
          label: 'Сбросить',
          variant: 'dangerOutline',
          onClick: () => {
            resetColumnOrderToDefault?.();
            setResetLayoutDialogOpen(false);
          },
        },
      ]}
    />
    </>
  );
}
